/**
 * Short links — `app.hiive.club/l/bK7mq9Wt` — and the reasoning behind every
 * constant in here.
 *
 * WHY A TABLE AND NOT A SERVICE. Nothing in the self-hosted shortener world
 * ships as a library you can import: Dub needs MySQL + Tinybird + Upstash +
 * R2 and starts at $90/mo hosted, Shlink and YOURLS are PHP services with
 * their own databases, Sink is a separate Cloudflare Worker. One table plus
 * one Route Handler is smaller than any of them and reuses the redirect
 * pattern `/r/`, `/c/` and `/s/` already established.
 *
 * WHY `app.hiive.club` AND NOT A SHORT DOMAIN. A short domain has to host its
 * own `apple-app-site-association` and `assetlinks.json` — Apple refuses an
 * AASA served through any redirect, Android fails verification on a 3xx, and
 * since Android 12 an unverified App Link opens the browser silently with no
 * error to notice. `app.hiive.club` is already a verified associated domain on
 * both platforms, so `/l/` costs one prefix in
 * `src/lib/native/app-links.ts` and nothing else. Cutting `?ref=share&
 * ref_from=…` and the entity slug already takes a shared event link from 88
 * characters to 33.
 *
 * ─── THE CODE SPACE ────────────────────────────────────────────────────────
 *
 * Sized so the URL format never has to be revised, at any volume.
 *
 * Codes are random, never a counter and never a reversible encoding of the
 * row id: sequential codes let anyone walk `/l/1`, `/l/2`, … and read off both
 * the total link count and every destination, some of which point at unlisted
 * pages. `sqids`/`hashids` have the same problem and say so in their own docs.
 *
 * With a UNIQUE index on `code` plus insert-retry, the birthday probability
 * stops mattering — what matters is how often a retry happens, which is the
 * load factor of the length being minted at. So the length ESCALATES on each
 * collision (see `mintShortCode`), and the whole space is available without a
 * migration, a config change, or a second URL format:
 *
 *   length  slots      links at a 1% load factor
 *   8       2.8e12     2.8e10
 *   9       1.0e14     1.0e12   ← a trillion links live here
 *   10      3.7e15     3.7e13
 *   12      4.7e18     4.7e16
 *   14      6.1e21     6.1e19
 *
 * A trillion links therefore settles at nine or ten characters on its own,
 * with no ceremony: the first 8-character candidate collides, the retry is
 * minted at 9 in a space that is 36x emptier, and the expected number of
 * attempts stays a hair above 1 the entire way up. The real ceiling is
 * libSQL's storage, not this alphabet — and because dedupe is by DESTINATION
 * (see `target.ts`), a trillion shares of a million events is a million rows.
 */

/** The claimed path prefix. Mirrored in `src/lib/native/app-links.ts`. */
export const SHORT_LINK_PATH_PREFIX = "/l/";

/**
 * `nolookalikesSafe` from nanoid-dictionary, inlined rather than adding a
 * dependency for one string. Two deliberate exclusions:
 *
 *  - Ambiguous glyphs (`0 O o 1 l I 5 S s 2 Z u v`) — a code read off a screen
 *    or a printed QR fallback cannot be mistyped. Same discipline as
 *    REFERRAL_CODE_ALPHABET.
 *  - Every vowel — so a random code can never spell something obscene. These
 *    links get posted to Instagram and WhatsApp under the hiive's own name,
 *    and a profanity denylist you have to maintain is strictly worse than an
 *    alphabet that cannot produce a word in the first place.
 *
 * Costs one character of length versus base62 (36 symbols against 62). Worth
 * it: see the table above — nine characters still covers a trillion links.
 */
export const SHORT_CODE_ALPHABET = "6789BCDFGHJKLMNPQRTWbcdfghjkmnpqrtwz";

/** Where minting starts. Escalates from here, one character per collision. */
export const SHORT_CODE_MIN_LENGTH = 8;

/**
 * The longest code that will ever be minted — 6.1e21 slots. Not a scale limit
 * so much as a tripwire: reaching it means collisions are happening at a rate
 * that says something else is wrong (a broken RNG, a duplicated alphabet).
 */
export const SHORT_CODE_MAX_LENGTH = 14;

/** Length window accepted on the read path, before any database work. */
export const SHORT_CODE_PATTERN = new RegExp(
  `^[${SHORT_CODE_ALPHABET}]{${SHORT_CODE_MIN_LENGTH},${SHORT_CODE_MAX_LENGTH}}$`,
);

/**
 * How long a resolved code is cached in Redis.
 *
 * A link's destination is immutable, so this could be much longer — but
 * `disabledAt` is the moderation kill switch for a link that has been posted
 * somewhere it should not have been, and this TTL is the worst-case delay
 * before that takes effect. Five minutes is the trade.
 */
export const SHORT_LINK_RESOLVE_CACHE_TTL_SECONDS = 300;

/**
 * Misses are cached too, and that is not an optimisation — without it, walking
 * `/l/aaaaaaaa`, `/l/aaaaaaab`, … is an unauthenticated way to put one libSQL
 * query per request on the primary.
 */
export const SHORT_LINK_MISS_CACHE_TTL_SECONDS = 60;

/** Cache window for the Open Graph head served to link-preview crawlers. */
export const SHORT_LINK_PREVIEW_CACHE_TTL_SECONDS = 900;

/**
 * Path prefixes a short link is allowed to point at.
 *
 * The destination arrives from the client (the share sheet knows the URL it is
 * sharing, not an entity id), so this list is what stops `/l/` becoming an
 * open redirector on a domain that also holds the login page — the single
 * nastiest thing you can hand a phisher. Absolute URLs are rejected outright
 * in `target.ts`; this narrows it further to pages that are actually
 * shareable, so a minted row cannot point at `/sign-in`, `/queen/…` or any
 * checkout or auth handoff.
 */
export const SHAREABLE_TARGET_PREFIXES = [
  "/rewards/",
  "/hiives/",
  "/venues/",
  "/profile/",
  "/post/",
  "/discover",
  "/activities/",
  "/sessions/",
  "/movement/",
  "/shop/",
  "/invite/",
] as const;

/**
 * How long a resolution may be cached, given the row's expiry.
 *
 * Never past `expiresAt`. A poster QR expiring in two minutes, cached for the
 * full five, would keep resolving for three minutes after it was supposed to
 * stop — the one case where the cache can outlive the thing it caches, because
 * expiry is a timestamp the row already carries rather than an edit somebody
 * makes later. `disabledAt` is the opposite case and genuinely does wait out
 * the TTL, which is what `forgetShortLink` is for.
 *
 * `undefined` means the code did not resolve, and misses get the short window.
 */
export function shortLinkCacheTtlSeconds(
  expiresAt: Date | null | undefined,
): number {
  if (expiresAt === undefined) return SHORT_LINK_MISS_CACHE_TTL_SECONDS;
  if (expiresAt === null) return SHORT_LINK_RESOLVE_CACHE_TTL_SECONDS;

  const secondsLeft = Math.floor((expiresAt.getTime() - Date.now()) / 1000);
  // Floor of 1: Redis rejects a non-positive TTL, and a link this close to
  // expiry is about to be a miss anyway.
  return Math.max(
    1,
    Math.min(SHORT_LINK_RESOLVE_CACHE_TTL_SECONDS, secondsLeft),
  );
}
